import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { projectAPI, constantsAPI } from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import {
  Search,
  MapPin,
  TreeDeciduous,
  Filter,
  Plus,
  Ruler,
  Clock,
  ArrowRight,
  FileText,
  X,
  Leaf,
  SlidersHorizontal,
  Info,
} from 'lucide-react';

const BUNDESLAENDER = [
  "Baden-Württemberg", "Bayern", "Berlin", "Brandenburg", "Bremen",
  "Hamburg", "Hessen", "Mecklenburg-Vorpommern", "Niedersachsen",
  "Nordrhein-Westfalen", "Rheinland-Pfalz", "Saarland", "Sachsen",
  "Sachsen-Anhalt", "Schleswig-Holstein", "Thüringen"
];

const ProjectsPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    bundesland: searchParams.get('bundesland') || '',
    kategorie: searchParams.get('kategorie') || '',
    mine: searchParams.get('mine') === 'true',
    search: '',
    // Advanced filters
    minFlaeche: searchParams.get('minFlaeche') || '',
    maxFlaeche: searchParams.get('maxFlaeche') || '',
    status: searchParams.get('status') || '',
    baumart: searchParams.get('baumart') || ''
  });

  const isWaldbesitzer = user?.role === 'waldbesitzer';

  const renderProjectCard = (project) => (
    <Link key={project.id} to={`/projekte/${project.id}`} data-testid={`project-card-${project.id}`}>
      <Card className={`h-full hover:shadow-lift hover:-translate-y-1 transition-all duration-300 group border-l-4 ${project.is_demo ? 'border-l-transparent hover:border-l-gray-300' : 'border-l-primary/40 hover:border-l-primary'}`}>
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20">{project.kategorie_name}</Badge>
              {project.is_demo && (
                <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-400 border border-gray-200" data-testid="demo-badge">Beispiel</span>
              )}
            </div>
            {getStatusBadge(project.status)}
          </div>
          <h3 className="font-semibold text-lg text-gray-900 mb-2 group-hover:text-primary transition-colors line-clamp-2">{project.titel}</h3>
          <p className="text-sm text-gray-500 line-clamp-3 mb-4">{project.beschreibung}</p>
          <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{project.ort ? `${project.ort}, ` : ''}{project.bundesland}</span>
            <span className="flex items-center gap-1"><Ruler className="h-3 w-3" />{project.flaeche_ha} ha</span>
          </div>
          {project.baumarten?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-4">
              {project.baumarten.slice(0, 3).map((baumart) => (
                <Badge key={baumart} variant="secondary" className="bg-secondary/10 text-secondary text-xs">{baumart}</Badge>
              ))}
              {project.baumarten.length > 3 && <Badge variant="secondary" className="text-xs">+{project.baumarten.length - 3}</Badge>}
            </div>
          )}
          <div className="flex items-center justify-between pt-3 border-t border-warm-200">
            <span className="text-xs text-gray-400">{new Date(project.created_at).toLocaleDateString('de-DE')}</span>
            {project.angebote_count > 0 && <span className="text-xs text-amber-600 font-medium">{project.angebote_count} Angebote</span>}
          </div>
        </CardContent>
      </Card>
    </Link>
  );


  const BAUMARTEN = [
    "Fichte", "Kiefer", "Buche", "Eiche", "Tanne", "Lärche", "Douglasie",
    "Birke", "Ahorn", "Esche", "Erle", "Pappel", "Mischbestand"
  ];

  const STATUS_OPTIONS = [
    { id: 'offen', name: 'Offen' },
    { id: 'angebote_erhalten', name: 'Mit Angeboten' },
    { id: 'vergeben', name: 'Vergeben' },
    { id: 'in_arbeit', name: 'In Arbeit' },
    { id: 'abgeschlossen', name: 'Abgeschlossen' }
  ];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.bundesland, filters.kategorie, filters.mine]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [projectsRes, kategorienRes] = await Promise.all([
        projectAPI.list({
          bundesland: filters.bundesland || undefined,
          kategorie: filters.kategorie || undefined,
          mine: filters.mine || undefined
        }),
        constantsAPI.getKategorien()
      ]);
      
      setProjects(projectsRes.data);
      setKategorien(kategorienRes.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error(t('toasts.projekteLadeFehler'));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    const params = new URLSearchParams();
    if (newFilters.bundesland) params.set('bundesland', newFilters.bundesland);
    if (newFilters.kategorie) params.set('kategorie', newFilters.kategorie);
    if (newFilters.mine) params.set('mine', 'true');
    if (newFilters.minFlaeche) params.set('minFlaeche', newFilters.minFlaeche);
    if (newFilters.maxFlaeche) params.set('maxFlaeche', newFilters.maxFlaeche);
    if (newFilters.status) params.set('status', newFilters.status);
    if (newFilters.baumart) params.set('baumart', newFilters.baumart);
    setSearchParams(params);
  };

  const clearAdvancedFilters = () => {
    setFilters(prev => ({
      ...prev,
      minFlaeche: '',
      maxFlaeche: '',
      status: '',
      baumart: ''
    }));
    setShowAdvancedFilters(false);
  };

  const activeAdvancedFiltersCount = [
    filters.minFlaeche,
    filters.maxFlaeche,
    filters.status,
    filters.baumart
  ].filter(Boolean).length;

  const filteredProjects = projects.filter(p => {
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch = (
        p.titel.toLowerCase().includes(searchLower) ||
        p.beschreibung.toLowerCase().includes(searchLower) ||
        p.bundesland.toLowerCase().includes(searchLower)
      );
      if (!matchesSearch) return false;
    }
    
    // Area filter
    if (filters.minFlaeche && p.flaeche_ha < parseFloat(filters.minFlaeche)) {
      return false;
    }
    if (filters.maxFlaeche && p.flaeche_ha > parseFloat(filters.maxFlaeche)) {
      return false;
    }
    
    // Status filter
    if (filters.status && p.status !== filters.status) {
      return false;
    }
    
    // Tree type filter
    if (filters.baumart && !p.baumarten?.includes(filters.baumart)) {
      return false;
    }
    
    return true;
  });

  const getStatusBadge = (status) => {
    const config = {
      offen: { label: 'Offen', className: 'bg-blue-100 text-blue-700' },
      angebote_erhalten: { label: 'Angebote', className: 'bg-amber-100 text-amber-700' },
      vergeben: { label: 'Vergeben', className: 'bg-purple-100 text-purple-700' },
      in_arbeit: { label: 'In Arbeit', className: 'bg-orange-100 text-orange-700' },
      abgeschlossen: { label: 'Fertig', className: 'bg-green-100 text-green-700' },
    };
    const c = config[status] || { label: status, className: 'bg-gray-100 text-gray-700' };
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="projects-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl font-bold text-gray-900">
              {filters.mine ? 'Meine Projekte' : 'Waldprojekte'}
            </h1>
            <p className="text-gray-500 mt-1">
              {filters.mine 
                ? 'Verwalten Sie Ihre Projekte'
                : 'Finden Sie passende Projekte in Ihrer Region'}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {isWaldbesitzer && (
              <>
                <Button
                  variant={filters.mine ? 'default' : 'outline'}
                  onClick={() => handleFilterChange('mine', !filters.mine)}
                  className={filters.mine ? 'bg-primary' : ''}
                >
                  Meine Projekte
                </Button>
                <Link to="/projekte/neu">
                  <Button className="bg-primary hover:bg-primary-600">
                    <Plus className="mr-2 h-4 w-4" />
                    Neues Projekt
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={t('placeholders.projekteSuchen')}
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                  data-testid="projects-search"
                />
              </div>
              
              <Select
                value={filters.bundesland || 'all'}
                onValueChange={(v) => handleFilterChange('bundesland', v === 'all' ? '' : v)}
              >
                <SelectTrigger className="w-full md:w-48" data-testid="filter-bundesland">
                  <SelectValue placeholder={t('placeholders.bundesland')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Bundesländer</SelectItem>
                  {BUNDESLAENDER.map((land) => (
                    <SelectItem key={land} value={land}>{land}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.kategorie || 'all'}
                onValueChange={(v) => handleFilterChange('kategorie', v === 'all' ? '' : v)}
              >
                <SelectTrigger className="w-full md:w-48" data-testid="filter-kategorie">
                  <SelectValue placeholder={t('placeholders.kategorie')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Kategorien</SelectItem>
                  {kategorien.map((kat) => (
                    <SelectItem key={kat.id} value={kat.id}>{kat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Advanced Filters Button */}
              <Sheet open={showAdvancedFilters} onOpenChange={setShowAdvancedFilters}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="relative" data-testid="advanced-filters-btn">
                    <SlidersHorizontal className="h-4 w-4 mr-2" />
                    Erweiterte Filter
                    {activeAdvancedFiltersCount > 0 && (
                      <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center bg-primary text-white text-xs">
                        {activeAdvancedFiltersCount}
                      </Badge>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>Erweiterte Filter</SheetTitle>
                  </SheetHeader>
                  
                  <div className="space-y-6 mt-6">
                    {/* Area Range */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Fläche (ha)</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-gray-500">Min</Label>
                          <Input
                            type="number"
                            placeholder="0"
                            value={filters.minFlaeche}
                            onChange={(e) => handleFilterChange('minFlaeche', e.target.value)}
                            data-testid="filter-min-flaeche"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-500">Max</Label>
                          <Input
                            type="number"
                            placeholder="∞"
                            value={filters.maxFlaeche}
                            onChange={(e) => handleFilterChange('maxFlaeche', e.target.value)}
                            data-testid="filter-max-flaeche"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Status Filter */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Projektstatus</Label>
                      <Select
                        value={filters.status || 'all'}
                        onValueChange={(v) => handleFilterChange('status', v === 'all' ? '' : v)}
                      >
                        <SelectTrigger data-testid="filter-status">
                          <SelectValue placeholder={t('placeholders.alleStatus')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Alle Status</SelectItem>
                          {STATUS_OPTIONS.map((status) => (
                            <SelectItem key={status.id} value={status.id}>{status.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Tree Type Filter */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Baumart</Label>
                      <Select
                        value={filters.baumart || 'all'}
                        onValueChange={(v) => handleFilterChange('baumart', v === 'all' ? '' : v)}
                      >
                        <SelectTrigger data-testid="filter-baumart">
                          <SelectValue placeholder={t('placeholders.alleBaumarten')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Alle Baumarten</SelectItem>
                          {BAUMARTEN.map((baumart) => (
                            <SelectItem key={baumart} value={baumart}>{baumart}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Clear Filters */}
                    {activeAdvancedFiltersCount > 0 && (
                      <Button 
                        variant="outline" 
                        className="w-full" 
                        onClick={clearAdvancedFilters}
                        data-testid="clear-filters-btn"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Filter zurücksetzen
                      </Button>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Active Filters Display */}
            {activeAdvancedFiltersCount > 0 && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
                {filters.minFlaeche && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Min. {filters.minFlaeche} ha
                    <X className="h-3 w-3 cursor-pointer" onClick={() => handleFilterChange('minFlaeche', '')} />
                  </Badge>
                )}
                {filters.maxFlaeche && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Max. {filters.maxFlaeche} ha
                    <X className="h-3 w-3 cursor-pointer" onClick={() => handleFilterChange('maxFlaeche', '')} />
                  </Badge>
                )}
                {filters.status && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    {STATUS_OPTIONS.find(s => s.id === filters.status)?.name}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => handleFilterChange('status', '')} />
                  </Badge>
                )}
                {filters.baumart && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <Leaf className="h-3 w-3" />
                    {filters.baumart}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => handleFilterChange('baumart', '')} />
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results Count */}
        <p className="text-sm text-gray-500">
          {filteredProjects.length} Projekte gefunden
        </p>

        {/* Projects List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-pulse text-primary">Laden...</div>
          </div>
        ) : filteredProjects.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Keine Projekte gefunden</h3>
              <p className="text-gray-500 mb-4">
                {filters.mine 
                  ? 'Sie haben noch keine Projekte erstellt'
                  : 'Versuchen Sie andere Filter'}
              </p>
              {isWaldbesitzer && (
                <Link to="/projekte/neu">
                  <Button className="bg-primary hover:bg-primary-600">
                    <Plus className="mr-2 h-4 w-4" />
                    Erstes Projekt erstellen
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Echte Projekte */}
            {filteredProjects.filter(p => !p.is_demo).length > 0 && (
              <>
                <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide flex items-center gap-2" data-testid="section-real-projects">
                  <FileText className="h-4 w-4 text-primary" />
                  Ausgeschriebene Projekte
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProjects.filter(p => !p.is_demo).map((project) => renderProjectCard(project))}
                </div>
              </>
            )}

            {/* Beispielprojekte */}
            {filteredProjects.filter(p => p.is_demo).length > 0 && (
              <>
                <div className="flex items-start gap-3 rounded-lg border border-warm-200 bg-warm-50 px-4 py-3 mt-2" data-testid="projekte-beispiel-hint">
                  <Info className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-gray-600">
                    Die folgenden <strong>Beispielprojekte</strong> zeigen typische Ausschreibungen auf ForestHub.
                    Als registrierter Waldbesitzer können Sie eigene Projekte erstellen und Angebote erhalten.
                  </div>
                </div>
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2" data-testid="section-demo-projects">
                  <Info className="h-4 w-4 text-muted-foreground" />
                  Beispielprojekte
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProjects.filter(p => p.is_demo).map((project) => renderProjectCard(project))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default ProjectsPage;
